/**
 * Scoring helpers shared by the results pages and the Mock Performance Tracker.
 *
 * Attempts are stored as raw correct/total counts only, so every score, grade
 * and band shown to a student is derived here.
 */

export const GRADE_SCALE = [
  { grade: "A1", min: 75 },
  { grade: "B2", min: 70 },
  { grade: "B3", min: 65 },
  { grade: "C4", min: 60 },
  { grade: "C5", min: 55 },
  { grade: "C6", min: 50 },
  { grade: "D7", min: 45 },
  { grade: "E8", min: 40 },
  { grade: "F9", min: 0 },
];

/** Best grade first, so the chart can plot A1 at the top of the axis. */
export const GRADE_ORDER = GRADE_SCALE.map((band) => band.grade);

export const GRADE_BANDS = [
  { id: "distinction", label: "A1 — Distinction", color: "#16a34a", grades: ["A1"] },
  { id: "credit-high", label: "B2/B3 — Credit", color: "#2563eb", grades: ["B2", "B3"] },
  { id: "credit", label: "C4–C6 — Credit", color: "#f59e0b", grades: ["C4", "C5", "C6"] },
  { id: "pass", label: "D7/E8 — Pass", color: "#ea580c", grades: ["D7", "E8"] },
  { id: "fail", label: "F9 — Fail", color: "#dc2626", grades: ["F9"] },
];

export function toGrade(percent) {
  const band = GRADE_SCALE.find((entry) => percent >= entry.min);
  return band ? band.grade : "F9";
}

/** Numeric position for the grade axis: F9 = 0 at the bottom, A1 = 8 at the top. */
export function gradeToAxisValue(grade) {
  const index = GRADE_ORDER.indexOf(grade);
  if (index === -1) return 0;
  return GRADE_ORDER.length - 1 - index;
}

export function axisValueToGrade(value) {
  const index = GRADE_ORDER.length - 1 - Math.round(value);
  return GRADE_ORDER[index] || "";
}

export function gradeColor(grade) {
  const band = GRADE_BANDS.find((entry) => entry.grades.includes(grade));
  return band ? band.color : "#64748b";
}

/** A grade is "better" when it sits higher on the axis. */
export function bestGrade(grades) {
  return grades.reduce((best, grade) => {
    if (!best) return grade;
    return gradeToAxisValue(grade) > gradeToAxisValue(best) ? grade : best;
  }, null);
}

/** JAMB UTME is marked out of 400 across the four subjects of an attempt. */
export function toJambScore(percent) {
  return Math.round((percent / 100) * 400);
}

export const TOPIC_BANDS = [
  { id: "very-strong", label: "Very Strong", min: 80, color: "#16a34a" },
  { id: "strong", label: "Strong", min: 60, color: "#2563eb" },
  { id: "average", label: "Average", min: 40, color: "#f59e0b" },
  { id: "weak", label: "Weak", min: 0, color: "#ef4444" },
];

export const TOPIC_BAND_ORDER = ["weak", "average", "strong", "very-strong"];

export function getAccuracyBand(correct, incorrect) {
  const attempted = (correct || 0) + (incorrect || 0);
  const percent = attempted > 0 ? (correct / attempted) * 100 : 0;
  const band = TOPIC_BANDS.find((entry) => percent >= entry.min);
  return { ...band, percent: Math.round(percent), attempted };
}
